import React, { useContext, useState, useMemo, useCallback } from 'react';
import { DateTime } from 'luxon';
import { TrafficService } from 'core/api/services/trafficService';

const TrafficContext = React.createContext(null);

export const TrafficProvider = ({ children }) => {
    const [loading, setLoading] = useState(false);
    const [trafficData, setTrafficData] = useState(null);

    // Direct state for the extracted data
    const [visits, setVisits] = useState([]);
    const [visitsTable, setVisitsTable] = useState([]);
    const [visitDaily, setVisitDaily] = useState({});

    const [filters, setFilters] = useState({
        startDate: DateTime.utc().startOf('month'),
        endDate: DateTime.utc().endOf('month'),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Bogota',
        contractIds: [],
    });

    const updateFilters = useCallback((newFilters) => {
        setFilters((prevFilters) => ({
            ...prevFilters,
            ...newFilters,
        }));
    }, []);

    const updateFilter = useCallback((key, value) => {
        setFilters((prevFilters) => ({
            ...prevFilters,
            [key]: value,
        }));
    }, []);

    const resetFilters = useCallback(() => {
        setFilters({
            startDate: DateTime.utc().startOf('month'),
            endDate: DateTime.utc().endOf('month'),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Bogota',
            contractIds: [],
        });
    }, []);

    const getDashboard = useCallback(
        async (options = {}) => {
            try {
                setLoading(true);
                const requestOptions = {
                    ...options,
                    filters: { ...filters, ...options.filters },
                };

                const result = await TrafficService.getDashboard(requestOptions);

                if (!result) {
                    throw new Error('Failed to fetch traffic dashboard data');
                }

                setTrafficData(result);

                // Update individual state values
                setVisits(result?.visits ?? []);
                setVisitsTable(result?.dashboard?.visitsTable ?? []);
                setVisitDaily(result?.dashboard?.visitDaily ?? {});

                return true;
            } catch (err) {
                console.error(err);
                return false;
            } finally {
                setLoading(false);
            }
        },
        [filters]
    );

    // Extract meta information - memoized to update when trafficData changes
    const meta = useMemo(() => trafficData?.meta ?? {}, [trafficData]);
    const errors = useMemo(() => trafficData?.errors ?? [], [trafficData]);

    // Memoize the context value to prevent unnecessary re-renders
    const contextValue = useMemo(
        () => ({
            loading,
            trafficData,
            visits,
            visitsTable,
            visitDaily,
            meta,
            errors,
            filters,
            getDashboard,
            updateFilters,
            updateFilter,
            resetFilters,
            setFilters,
            setTrafficData,
        }),
        [loading, trafficData, visits, visitsTable, visitDaily, meta, errors, filters, getDashboard, updateFilters, updateFilter, resetFilters, setFilters, setTrafficData]
    );

    return <TrafficContext.Provider value={contextValue}>{children}</TrafficContext.Provider>;
};

TrafficProvider.displayName = 'TrafficProvider';

export const useTraffic = () => useContext(TrafficContext);
